import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, Close, Edit, ExpandMore, InfoOutlined, PowerSettingsNew, Restore } from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import AddButton from "../components/AddButton";
import ClearFiltersButton from "../components/ClearFiltersButton";
import ResultCount from "../components/ResultCount";
import ConfirmModal from "../components/modals/ConfirmModal";
import SavingOverlay from "../components/SavingOverlay";
import { useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { colors, typography } from "../theme/designSystem";
import { filterFieldSx, searchFieldSx } from "../theme/filterStyles";
import { accountCategoriesService, AccountCategory } from "../services/accountCategoriesService";

// Plano de contas do livro-caixa (item 143, 14/09/2026): manutenção em três níveis —
// tipo (Receitas/Despesas/Impostos, raiz) › categoria › subcategoria. O tipo é fixo do produto;
// a clínica cria e edita categorias e subcategorias. Nada é apagado: linha com lançamento só
// inativa (o histórico do livro aponta para ela). A lista é uma tabela com acordeão por categoria
// (PADRÃO DESIGN, item 13): cabeçalho no painel, filtros reativos, c4u-table com data-label.

const NAME_MAX = 120;
const DESC_MAX = 300;
const COST_MAX = 30;

const normalize = (value: string) => value.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

interface FormState {
  id: string | null;
  level: "category" | "sub";
  type: string;
  description: string;
  parentTypeId: string;
  parentCategoryId: string;
  dre: boolean;
  costCenterCode: string;
}

const emptyForm = (level: FormState["level"], parentTypeId = "", parentCategoryId = ""): FormState => ({
  id: null, level, type: "", description: "", parentTypeId, parentCategoryId, dre: true, costCenterCode: "",
});

const AccountCategories: React.FC = () => {
  const { navigateTo } = useRouter();
  const { user } = useAuth();
  const [items, setItems] = useState<AccountCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<"ativos" | "inativos" | "todos">("ativos");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [form, setForm] = useState<FormState | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [toToggle, setToToggle] = useState<AccountCategory | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await accountCategoriesService.list());
    } catch {
      setError("Não foi possível carregar o plano de contas.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const roots = useMemo(
    () => items.filter((i) => !i.parentTypeId && !i.parentCategoryId).sort((a, b) => a.type.localeCompare(b.type)),
    [items],
  );
  const categories = useMemo(
    () => items.filter((i) => i.parentTypeId && !i.parentCategoryId).sort((a, b) => a.type.localeCompare(b.type)),
    [items],
  );
  const subs = useMemo(() => items.filter((i) => i.parentCategoryId), [items]);

  const matchesStatus = (i: AccountCategory) =>
    statusFilter === "todos" || (statusFilter === "ativos" ? i.active : !i.active);
  const term = normalize(search.trim());
  const matchesTerm = (i: AccountCategory) => !term || normalize(i.type).includes(term) || normalize(i.description || "").includes(term);

  // categoria aparece se ela ou alguma subcategoria casa com o filtro; o tipo filtra pela raiz
  const visibleCategories = useMemo(
    () =>
      categories.filter((c) => {
        if (typeFilter && c.parentTypeId !== typeFilter) return false;
        const own = matchesStatus(c) && matchesTerm(c);
        const anySub = subs.some((s) => s.parentCategoryId === c.id && matchesStatus(s) && matchesTerm(s));
        return own || anySub;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [categories, subs, typeFilter, statusFilter, term],
  );
  const subsOf = (c: AccountCategory) =>
    subs.filter((s) => s.parentCategoryId === c.id && matchesStatus(s) && (!term || matchesTerm(s) || matchesTerm(c)))
      .sort((a, b) => a.type.localeCompare(b.type));
  const rootName = (id: string | null) => roots.find((r) => r.id === id)?.type || "—";

  const hasFilterChanges = search !== "" || typeFilter !== "" || statusFilter !== "ativos";
  const clearFilters = () => {
    setSearch("");
    setTypeFilter("");
    setStatusFilter("ativos");
  };

  const openNewCategory = () => {
    setFormError(null);
    setForm(emptyForm("category", typeFilter || roots[0]?.id || ""));
  };
  const openNewSub = (c: AccountCategory) => {
    setFormError(null);
    setForm(emptyForm("sub", c.parentTypeId || "", c.id));
  };
  const openEdit = (i: AccountCategory) => {
    setFormError(null);
    setForm({
      id: i.id,
      level: i.parentCategoryId ? "sub" : "category",
      type: i.type,
      description: i.description || "",
      parentTypeId: i.parentTypeId || "",
      parentCategoryId: i.parentCategoryId || "",
      dre: i.dre !== false,
      costCenterCode: i.costCenterCode || "",
    });
  };

  const save = async () => {
    if (!form) return;
    if (!form.type.trim()) {
      setFormError("Informe o nome.");
      return;
    }
    if (!form.parentTypeId) {
      setFormError("Escolha o tipo.");
      return;
    }
    if (form.level === "sub" && !form.parentCategoryId) {
      setFormError("Escolha a categoria.");
      return;
    }
    setSaving(true);
    try {
      if (form.id) {
        await accountCategoriesService.update(form.id, {
          type: form.type.trim(),
          description: form.description.trim() || null,
          dre: form.dre,
          costCenterCode: form.costCenterCode.trim(),
        });
      } else {
        await accountCategoriesService.create({
          type: form.type.trim(),
          description: form.description.trim() || null,
          parentTypeId: form.parentTypeId,
          parentCategoryId: form.level === "sub" ? form.parentCategoryId : null,
          dre: form.dre,
          costCenterCode: form.costCenterCode.trim() || null,
        });
        if (form.level === "sub") setExpanded((prev) => ({ ...prev, [form.parentCategoryId]: true }));
      }
      setForm(null);
      await load();
    } catch (e: any) {
      setFormError(e?.response?.data?.description || "Não foi possível salvar.");
    } finally {
      setSaving(false);
    }
  };

  // inativar/reativar é PUT active: preserva o histórico e some dos combos do Livro-caixa
  const toggleActive = async () => {
    if (!toToggle) return;
    setSaving(true);
    try {
      await accountCategoriesService.update(toToggle.id, {
        type: toToggle.type,
        description: toToggle.description,
        dre: toToggle.dre,
        costCenterCode: toToggle.costCenterCode || "",
        active: !toToggle.active,
      });
      setToToggle(null);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.description || "Não foi possível alterar a situação.");
      setToToggle(null);
    } finally {
      setSaving(false);
    }
  };

  const shown = visibleCategories.length + visibleCategories.reduce((n, c) => n + subsOf(c).length, 0);
  const rowActionSx = (background: string, hover: string) => ({ backgroundColor: background, "&:hover": { backgroundColor: hover } });

  const renderRow = (i: AccountCategory, isSub: boolean, subCount = 0) => (
    <Box key={i.id} className="c4u-table__row" sx={isSub ? { backgroundColor: "#fbfcfd" } : undefined}>
      <Box className="admin-plans-cell" data-label={isSub ? "Subcategoria" : "Categoria"} sx={{ flex: "1 1 240px", minWidth: 0, pl: isSub ? 4 : 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
          {!isSub && (
            <IconButton
              size="small"
              onClick={() => setExpanded((prev) => ({ ...prev, [i.id]: !prev[i.id] }))}
              aria-label={expanded[i.id] ? "Recolher" : "Expandir"}
              sx={{ padding: "2px", visibility: subCount > 0 ? "visible" : "hidden" }}
            >
              <ExpandMore sx={{ fontSize: "1.1rem", transform: expanded[i.id] ? "rotate(180deg)" : "none", transition: "transform .2s" }} />
            </IconButton>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography className="admin-plans-name" sx={{ fontSize: 13, fontWeight: isSub ? 400 : 600, overflowWrap: "anywhere" }}>
              {i.type}
              {!isSub && subCount > 0 && (
                <Typography component="span" sx={{ fontSize: 11, color: "#adb5bd", ml: 0.75 }}>{subCount} sub</Typography>
              )}
            </Typography>
            {i.description && (
              <Typography sx={{ fontSize: 11, color: "#6c757d", overflowWrap: "anywhere" }}>{i.description}</Typography>
            )}
          </Box>
        </Box>
      </Box>
      <Box className="admin-plans-cell c4u-col--lg" data-label="Tipo" sx={{ flex: "0 1 130px", minWidth: 0 }}>
        <Typography sx={{ fontSize: 12, color: "#6c757d" }}>{rootName(i.parentTypeId)}</Typography>
      </Box>
      <Box className="admin-plans-cell c4u-col--xl" data-label="Centro de custo" sx={{ flex: "0 1 120px", minWidth: 0 }}>
        <Typography sx={{ fontSize: 12, color: "#6c757d" }}>{i.costCenterCode || "—"}</Typography>
      </Box>
      <Box className="admin-plans-cell c4u-col--md" data-label="DRE" sx={{ flex: "0 0 70px" }}>
        <Typography sx={{ fontSize: 12, color: i.dre === false ? "#adb5bd" : "#28a745", fontWeight: 600 }}>
          {i.dre === false ? "Não" : "Sim"}
        </Typography>
      </Box>
      <Box className="admin-plans-cell c4u-col--md" data-label="Lançamentos" sx={{ flex: "0 0 100px" }}>
        <Typography sx={{ fontSize: 12, color: "#6c757d", fontVariantNumeric: "tabular-nums" }}>{i.usageCount}</Typography>
      </Box>
      <Box className="admin-plans-cell" data-label="Situação" sx={{ flex: "0 0 90px" }}>
        <Typography sx={{ fontSize: 11, fontWeight: 600, color: i.active ? "#28a745" : "#adb5bd" }}>
          {i.active ? "Ativa" : "Inativa"}
        </Typography>
      </Box>
      <Box className="admin-plans-cell admin-plans-actions" data-label="Ações" sx={{ flex: "0 0 150px", marginLeft: "auto" }}>
        <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
          {!isSub && i.active && (
            <Tooltip title="Nova subcategoria" arrow>
              <IconButton className="c4u-btn-icon" onClick={() => openNewSub(i)} sx={rowActionSx(colors.success, "#218838")} aria-label="Nova subcategoria">
                <Add sx={{ fontSize: "1rem", color: colors.white }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Editar" arrow>
            <IconButton className="c4u-btn-icon" onClick={() => openEdit(i)} sx={rowActionSx(colors.primary, "#029AAB")} aria-label="Editar">
              <Edit sx={{ fontSize: "1rem", color: colors.white }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={i.active ? "Inativar" : "Reativar"} arrow>
            <IconButton
              className="c4u-btn-icon"
              onClick={() => setToToggle(i)}
              sx={rowActionSx(i.active ? "#dc3545" : "#6c757d", i.active ? "#c82333" : "#5a6268")}
              aria-label={i.active ? "Inativar" : "Reativar"}
            >
              {i.active ? <PowerSettingsNew sx={{ fontSize: "1rem", color: colors.white }} /> : <Restore sx={{ fontSize: "1rem", color: colors.white }} />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", backgroundColor: "#f8f9fa" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={() => navigateTo("dashboard")}
      />

      <Box component="main" className="c4u-main">
        <Container maxWidth={false} disableGutters>
          <Box className="c4u-page-header c4u-page-header--in-panel">
            <Box>
              <Typography variant="h4" className="c4u-page-header__title">
                Categorias de lançamento
              </Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                O plano de contas do Livro-caixa em três níveis: tipo, categoria e subcategoria. É daqui que saem
                os combos de lançamento, a DRE e o Cenário de caixa.
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FaqButton />
            </Box>
          </Box>

          <div className="c4u-panel">
            <div className="c4u-filters__row no-print">
              <div className="c4u-filters__fields">
                <TextField
                  size="small"
                  label="Busca"
                  placeholder="Nome ou descrição"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  fullWidth
                  sx={searchFieldSx}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ maxLength: NAME_MAX }}
                />
                <TextField select size="small" label="Tipo" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} fullWidth sx={filterFieldSx} InputLabelProps={{ shrink: true }}>
                  <MenuItem value="">Todos</MenuItem>
                  {roots.map((r) => (
                    <MenuItem key={r.id} value={r.id}>{r.type}</MenuItem>
                  ))}
                </TextField>
                <TextField select size="small" label="Situação" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)} fullWidth sx={filterFieldSx} InputLabelProps={{ shrink: true }}>
                  <MenuItem value="ativos">Ativas</MenuItem>
                  <MenuItem value="inativos">Inativas</MenuItem>
                  <MenuItem value="todos">Todas</MenuItem>
                </TextField>
              </div>
              <div className="c4u-filters__actions">
                <ClearFiltersButton onClick={clearFilters} disabled={!hasFilterChanges || loading} />
                <AddButton onClick={openNewCategory} title="Nova categoria" />
              </div>
            </div>

            <Box className="c4u-info-box">
              <InfoOutlined className="c4u-info-box__icon" sx={{ fontSize: "1.1rem" }} />
              <Typography>
                Os tipos (Receitas, Despesas, Impostos) são fixos. Impostos fica separado de Despesas de propósito: na DRE,
                imposto sobre serviço é dedução da receita bruta, linha própria antes das despesas operacionais, como manda a
                prática contábil. Categoria ou subcategoria com lançamento não é excluída:
                inative, e ela some dos combos sem mexer no histórico. Os nomes "Atendim. clínica", "SALDO EM CONTA",
                "Impostos" e "Tranferência bancária" são lidos pelos relatórios; renomear muda o Fluxo de Caixa, a DRE e o Cenário.
              </Typography>
            </Box>

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress size={28} sx={{ color: colors.primary }} />
              </Box>
            ) : error ? (
              <Typography sx={{ textAlign: "center", color: "#dc3545", py: 4 }}>{error}</Typography>
            ) : (
              <>
                <ResultCount shown={shown} total={items.length - roots.length} />
                <Box className="c4u-table">
                  <Box className="c4u-table__header">
                    <Box className="admin-plans-header-cell" sx={{ flex: "1 1 240px" }}>Categoria / subcategoria</Box>
                    <Box className="admin-plans-header-cell c4u-col--lg" sx={{ flex: "0 1 130px" }}>Tipo</Box>
                    <Box className="admin-plans-header-cell c4u-col--xl" sx={{ flex: "0 1 120px" }}>Centro de custo</Box>
                    <Box className="admin-plans-header-cell c4u-col--md" sx={{ flex: "0 0 70px" }}>DRE</Box>
                    <Box className="admin-plans-header-cell c4u-col--md" sx={{ flex: "0 0 100px" }}>Lançamentos</Box>
                    <Box className="admin-plans-header-cell" sx={{ flex: "0 0 90px" }}>Situação</Box>
                    <Box className="admin-plans-header-cell admin-plans-actions" sx={{ flex: "0 0 150px", marginLeft: "auto" }}>Ações</Box>
                  </Box>
                  {visibleCategories.length === 0 ? (
                    <Typography sx={{ px: 2, py: 3, fontSize: 13, color: "#adb5bd", textAlign: "center" }}>Nenhuma categoria para os filtros.</Typography>
                  ) : (
                    visibleCategories.map((c) => {
                      const children = subsOf(c);
                      return (
                        <React.Fragment key={c.id}>
                          {renderRow(c, false, children.length)}
                          {expanded[c.id] && children.map((s) => renderRow(s, true))}
                        </React.Fragment>
                      );
                    })
                  )}
                </Box>
              </>
            )}
          </div>
        </Container>
      </Box>

      <Dialog open={form !== null} onClose={() => !saving && setForm(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "12px", position: "relative", overflow: "hidden" } }}>
        <SavingOverlay open={saving} message="Salvando..." />
        <DialogTitle sx={{ backgroundColor: colors.primary, color: colors.white, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "1rem 1.25rem" }}>
          <Typography component="h3" sx={{ fontSize: "1.1rem", fontWeight: typography.fontWeight.semibold, margin: 0 }}>
            {form?.id ? (form.level === "sub" ? "Editar subcategoria" : "Editar categoria") : form?.level === "sub" ? "Nova subcategoria" : "Nova categoria"}
          </Typography>
          <IconButton onClick={() => setForm(null)} sx={{ color: colors.white, padding: "0.25rem" }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "1.25rem !important", paddingTop: "1.5rem !important" }}>
          {form && (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, columnGap: "12px", rowGap: "16px" }}>
              <TextField
                select
                size="small"
                label="Tipo"
                value={form.parentTypeId}
                onChange={(e) => setForm({ ...form, parentTypeId: e.target.value, parentCategoryId: "" })}
                disabled={Boolean(form.id) || form.level === "sub"}
                InputLabelProps={{ shrink: true }}
                sx={{ gridColumn: { xs: "auto", sm: form.level === "sub" ? "auto" : "1 / -1" } }}
              >
                {roots.map((r) => (
                  <MenuItem key={r.id} value={r.id}>{r.type}</MenuItem>
                ))}
              </TextField>
              {form.level === "sub" && (
                <TextField
                  select
                  size="small"
                  label="Categoria"
                  value={form.parentCategoryId}
                  onChange={(e) => setForm({ ...form, parentCategoryId: e.target.value })}
                  disabled={Boolean(form.id)}
                  InputLabelProps={{ shrink: true }}
                >
                  {categories.filter((c) => c.active && c.parentTypeId === form.parentTypeId).map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.type}</MenuItem>
                  ))}
                </TextField>
              )}
              <TextField
                size="small"
                label="Nome"
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                required
                InputLabelProps={{ shrink: true }}
                inputProps={{ maxLength: NAME_MAX }}
                sx={{ gridColumn: { xs: "auto", sm: "1 / -1" } }}
              />
              <TextField
                size="small"
                label="Descrição"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                InputLabelProps={{ shrink: true }}
                inputProps={{ maxLength: DESC_MAX }}
                sx={{ gridColumn: { xs: "auto", sm: "1 / -1" } }}
              />
              <TextField
                size="small"
                label="Centro de custo"
                placeholder="Código da contabilidade, ex.: 3.01.001"
                value={form.costCenterCode}
                onChange={(e) => setForm({ ...form, costCenterCode: e.target.value })}
                InputLabelProps={{ shrink: true }}
                inputProps={{ maxLength: COST_MAX }}
              />
              <FormControlLabel
                control={<Switch checked={form.dre} onChange={(e) => setForm({ ...form, dre: e.target.checked })} />}
                label={<Typography sx={{ fontSize: 13 }}>Entra na DRE</Typography>}
                sx={{ alignSelf: "center" }}
              />
              {formError && (
                <Typography sx={{ gridColumn: { xs: "auto", sm: "1 / -1" }, fontSize: 13, color: "#dc3545" }}>{formError}</Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "1rem 1.25rem" }}>
          <Button variant="outlined" onClick={() => setForm(null)} sx={{ textTransform: "none" }}>Cancelar</Button>
          <Button variant="contained" onClick={save} sx={{ textTransform: "none", backgroundColor: colors.primary }}>Salvar</Button>
        </DialogActions>
      </Dialog>

      <FooterInternal className="login-footer-component" />

      <ConfirmModal
        isOpen={toToggle !== null}
        title={toToggle?.active ? "Inativar" : "Reativar"}
        message={
          toToggle?.active
            ? `Inativar "${toToggle?.type || ""}"? Ela some dos combos do Livro-caixa; os ${toToggle?.usageCount ?? 0} lançamento(s) existentes continuam apontando para ela.`
            : `Reativar "${toToggle?.type || ""}"? Ela volta aos combos do Livro-caixa.`
        }
        confirmButtonText={toToggle?.active ? "Inativar" : "Reativar"}
        cancelButtonText="Cancelar"
        type={toToggle?.active ? "danger" : "info"}
        isProcessing={saving}
        onConfirm={toggleActive}
        onCancel={() => setToToggle(null)}
      />
    </Box>
  );
};

export default AccountCategories;
